import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import omit from 'lodash/omit';

import Form from '../../components/Form';
import Input from '../../components/Input';
import Icon from '../../components/Icon';
import Button from '../../components/Button';
import Label from '../../components/Label';
import Message from '../../components/Message';
import PasswordRules from '../../components/PasswordRules';

const messages = defineMessages({
  username: {
    id: 'new-password.usernamePlaceholder',
    defaultMessage: 'E-mail Address',
  },
  verificationCode: {
    id: 'forgot-password.verificationCodePlaceholder',
    defaultMessage: 'Verification Code',
  },
  password: {
    id: 'forgot-password.passwordPlaceholder',
    defaultMessage: 'New Password',
  },
  confirmPassword: {
    id: 'forgot-password.confirmPasswordPlaceholder',
    defaultMessage: 'Confirm Password',
  },
});

function verifyForm(formData) {
  const errors = {};
  if (!formData.verification) {
    errors.verificationErrorLabel = (
      <Label role="tooltip" basic color="red" pointing>
        <FormattedMessage id="forgot-password.error.missingVerificationCode" defaultMessage="Please enter a verification code" />
      </Label>
    );
  }
  if (!formData.password) {
    errors.passwordErrorLabel = (
      <Label role="tooltip" basic color="red" pointing>
        <FormattedMessage id="forgot-password.error.missingPassword" defaultMessage="Please enter a password" />
      </Label>
    );
  }
  if (!formData.confirmPassword) {
    errors.confirmPasswordErrorLabel = (
      <Label role="tooltip" basic color="red" pointing>
        <FormattedMessage id="forgot-password.error.missingPassword" defaultMessage="Please enter a password" />
      </Label>
    );
  }
  if (formData.password !== formData.confirmPassword) {
    errors.errorMessage = (
      <Message role="alert" negative data-cy="forgot-password-mismatched-pass-tooltip">
        <Message.Header>
          <FormattedMessage id="forgot-password.error.mismatchedPassword" defaultMessage="Passwords must match" />
        </Message.Header>
      </Message>
    );
  }
  return errors;
}

class VerifySetPassword extends React.PureComponent {
  static propTypes = {
    intl: PropTypes.shape({
      formatMessage: PropTypes.func.isRequired,
    }).isRequired,
    username: PropTypes.string.isRequired,
    onSubmit: PropTypes.func.isRequired,
  };

  state = {
    errors: {
      verificationErrorLabel: null,
      passwordErrorLabel: null,
      confirmPasswordErrorLabel: null,
      errorMessage: null,
    },
  };

  _onChange = (e, { value, name } = {}) => {
    value = value || e.target.value;
    name = name || e.target.name;
    this.setState({ [name]: value });
  };

  _onSubmit = (e) => {
    e.preventDefault();
    const formData = omit(this.state, 'errors');
    const errors = verifyForm(formData);
    this.setState({ errors });
    if (!Object.keys(errors).length) {
      this.props.onSubmit(formData.verification, formData.password);
    }
  };

  render() {
    const { intl, username } = this.props;
    const { errors, verification, password, confirmPassword } = this.state;
    // can't beat the auto complete, but I'm sure going to try
    // https://developer.mozilla.org/en-US/docs/Web/Security/Securing_your_site/Turning_off_form_autocompletion#Disabling_autocompletion
    return (
      <Form onSubmit={this._onSubmit}>
        <Form.Field>
          <Input type="email" name="username" value={username} icon iconPosition="left">
            <Icon name="mail" />
            <input
              id="forgotten-email-input"
              disabled
              aria-label={intl.formatMessage(messages.username)}
              name="username"
              aria-disabled
              autoComplete="off"
            />
          </Input>
        </Form.Field>
        <Form.Field>
          <FormattedMessage
            id="forget-password.emailSent"
            defaultMessage="If the e-mail address entered is linked to an existing user account, a verification code will be sent. Enter the code in the field below."
          />
        </Form.Field>
        <Form.Field>
          <label htmlFor="forgotten-verification-input">
            <FormattedMessage {...messages.verificationCode} />
          </label>
          <Input type="number" name="verification" value={verification} icon iconPosition="left" onChange={this._onChange}>
            <Icon name="key" />
            <input
              id="forgotten-verification-input"
              aria-label={intl.formatMessage(messages.verificationCode)}
              autoComplete="off"
              aria-invalid={!!errors.verificationErrorLabel}
              required
              aria-required
            />
          </Input>
          { errors.verificationErrorLabel }
        </Form.Field>
        <Form.Field>
          <label htmlFor="forgotten-new-password-input">
            <FormattedMessage {...messages.password} />
          </label>
          <Input type="password" name="password" value={password} icon iconPosition="left" onChange={this._onChange}>
            <Icon name="lock" />
            <input
              id="forgotten-new-password-input"
              aria-label={intl.formatMessage(messages.password)}
              autoComplete="off"
              aria-invalid={!!errors.passwordErrorLabel}
              required
              aria-required
            />
          </Input>
          { errors.passwordErrorLabel }
          <PasswordRules />
        </Form.Field>
        <Form.Field>
          <label htmlFor="forgotten-confirm-password-input">
            <FormattedMessage {...messages.confirmPassword} />
          </label>
          <Input type="password" name="confirmPassword" value={confirmPassword} icon iconPosition="left" onChange={this._onChange}>
            <Icon name="lock" />
            <input
              id="forgotten-confirm-password-input"
              aria-label={intl.formatMessage(messages.confirmPassword)}
              required
              aria-invalid={!!errors.confirmPasswordErrorLabel}
              autoComplete="off"
              aria-required
            />
          </Input>
          { errors.confirmPasswordErrorLabel }
        </Form.Field>
        {errors.errorMessage}
        <Button primary type="submit" fluid data-cy="forgot-password-change-pass-button">
          <FormattedMessage id="forget-password.changePasswordButton" defaultMessage="Change Password" />
        </Button>
      </Form>
    );
  }
}

export default injectIntl(VerifySetPassword);
